import { useEffect, useState } from 'react';

import { User } from '../types/user.type';
import { UserRoles } from '../types/user-role.type';
import { AdminUpdateUserRequestDTO } from '../types/dtos/admin-update-user.dto';
import { UserFieldErrors, getUserFieldErrors } from '../utils/user-form-rules.util';

type EditableUserField = keyof AdminUpdateUserRequestDTO;

export type UserFieldChange = { field: EditableUserField; value: string | number | boolean };

export type UpdateUserFields = (editedFields: AdminUpdateUserRequestDTO) => Promise<User | null>;

type UseUserEditFormResult = {
  isEditing: boolean;
  updatedUser: User;
  editedFields: AdminUpdateUserRequestDTO;
  fieldErrors: UserFieldErrors;
  handleFieldChange: ({ field, value }: UserFieldChange) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: UpdateUserFields) => Promise<void>;
};

export function useUserEditForm(user: User): UseUserEditFormResult {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedUser, setUpdatedUser] = useState<User>(user);
  const [editedFields, setEditedFields] = useState<AdminUpdateUserRequestDTO>({});

  useEffect(() => {
    setUpdatedUser(user);
    setEditedFields({});
    setIsEditing(false);
  }, [user]);

  const handleCancelEditing = () => {
    setUpdatedUser(user);
    setEditedFields({});
    setIsEditing(false);
  };

  const handleSave = async (updateFn: UpdateUserFields) => {
    if (Object.keys(editedFields).length === 0) return;
    await updateFn(editedFields);
  };

  const handleFieldChange = ({ field, value }: UserFieldChange) => {
    const isLeavingEco = field === 'role' && value !== UserRoles.ECO;

    setIsEditing(true);
    setUpdatedUser((previousUser) => ({
      ...previousUser,
      [field]: value,
      ...(isLeavingEco ? { ecoPoints: user.ecoPoints } : {}),
    }));
    setEditedFields((previousFields) => {
      const nextFields = { ...previousFields, [field]: value };
      if (isLeavingEco) delete nextFields.ecoPoints;
      return nextFields;
    });
  };

  return {
    isEditing,
    updatedUser,
    editedFields,
    fieldErrors: getUserFieldErrors(editedFields),
    handleFieldChange,
    handleCancelEditing,
    handleSave,
  };
}
